import { useState } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, EstadoVacio, Seccion, Dato } from '@/components/Card';
import {
  verResumen,
  verDesgloseMensual,
  verVentas,
  resultadoPorReceta,
  type ResumenConDestinos,
} from '@/db/repos/reports';
import { verCuentasPorCobrar } from '@/db/repos/orders';
import { ETIQUETA_GRANULARIDAD, rangoDe, type Granularidad } from '@/domain/profit';
import {
  hoy,
  sumarDias,
  sumarMeses,
  formatearMedio,
  formatearCorto,
  etiquetaMes,
  type FechaLocal,
} from '@/domain/dates';
import { formatearCentavos } from '@/domain/money';

const GRANULARIDADES: Granularidad[] = ['dia', 'semana', 'mes'];

/**
 * Ganancias: lo que entro, lo que costo y lo que quedo.
 *
 * Se muestran DOS resultados y ninguno sobra:
 *
 *   ganancia de lo vendido -> el margen del platillo, para poner precios
 *   resultado del periodo  -> lo que quedo en la bolsa, contando lo no vendido
 *
 * Un dia en que se regalo media produccion puede tener buen margen y aun asi
 * haber perdido dinero. Mostrar solo uno de los dos numeros esconderia eso.
 */
export function ProfitPage() {
  const [granularidad, setGranularidad] = useState<Granularidad>('semana');
  const [ancla, setAncla] = useState<FechaLocal>(hoy());

  const resumen = useLiveQuery(() => verResumen(granularidad, ancla), [granularidad, ancla]);

  /**
   * Lo que le deben. Sin rango a proposito: una deuda vieja se sigue debiendo, y
   * el punto del aviso es que no confunda lo vendido con lo cobrado.
   */
  const porCobrar = useLiveQuery(() => verCuentasPorCobrar(), []);
  const rango = rangoDe(granularidad, ancla);
  const ventas = useLiveQuery(() => verVentas(rango.desde, rango.hasta), [rango.desde, rango.hasta]);
  const porReceta = useLiveQuery(
    () => resultadoPorReceta(rango.desde, rango.hasta),
    [rango.desde, rango.hasta],
  );
  const desglose = useLiveQuery(
    () => (granularidad === 'mes' ? verDesgloseMensual(ancla) : Promise.resolve(null)),
    [granularidad, ancla],
  );

  function mover(direccion: -1 | 1) {
    if (granularidad === 'dia') setAncla(sumarDias(ancla, direccion));
    else if (granularidad === 'semana') setAncla(sumarDias(ancla, direccion * 7));
    else setAncla(sumarMeses(ancla, direccion));
  }

  const titulo =
    granularidad === 'dia'
      ? formatearMedio(ancla)
      : granularidad === 'semana'
        ? `${formatearCorto(rango.desde)} – ${formatearCorto(rango.hasta)}`
        : etiquetaMes(ancla);

  return (
    <div className="space-y-6">
      <PageHeader titulo="Ganancias" descripcion="Cuánto entró, cuánto costó y cuánto quedó" />

      <div className="flex gap-2">
        {GRANULARIDADES.map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => setGranularidad(g)}
            aria-pressed={granularidad === g}
            className={`min-h-11 flex-1 rounded-xl border px-2 text-sm font-medium ${
              granularidad === g
                ? 'border-marca bg-marca text-white'
                : 'border-borde-fuerte bg-superficie text-texto active:bg-superficie-suave'
            }`}
          >
            {ETIQUETA_GRANULARIDAD[g]}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button variante="secundario" completo={false} onClick={() => mover(-1)}>
          ‹
        </Button>
        <p className="flex-1 text-center text-sm font-medium capitalize text-texto">{titulo}</p>
        <Button variante="secundario" completo={false} onClick={() => mover(1)}>
          ›
        </Button>
      </div>

      {ancla !== hoy() && (
        <Button variante="fantasma" onClick={() => setAncla(hoy())}>
          Volver a hoy
        </Button>
      )}

      {resumen === undefined && <p className="text-sm text-texto-suave">Cargando…</p>}

      {resumen !== undefined && resumen.cuantasVentas === 0 && (
        <EstadoVacio
          titulo="No hay ventas en este periodo"
          descripcion="Registra tus ventas y aquí aparece el resultado."
        />
      )}

      {resumen !== undefined && resumen.cuantasVentas > 0 && (
        <>
          <PanelResultado resumen={resumen} />

          {/*
            Vender no es cobrar. Esta pantalla suma lo VENDIDO, y una parte puede
            seguir sin pagar. Sin este aviso, "te quedo tanto" se lee como dinero
            en la bolsa cuando puede estar en la calle.
          */}
          {porCobrar !== undefined && porCobrar.totalCentavos > 0 && (
            <Banner tono="aviso" titulo="Ojo: vendido no es cobrado">
              Aquí arriba está lo que <strong>vendiste</strong>. De todo lo que llevas, hay{' '}
              <strong>{formatearCentavos(porCobrar.totalCentavos)}</strong> que todavía no te
              pagan, repartidos entre {porCobrar.clientes.length}{' '}
              {porCobrar.clientes.length === 1 ? 'persona' : 'personas'}.
              <div className="mt-3">
                <Link to="/pedidos">
                  <Button variante="secundario">Ver a quién le cobras</Button>
                </Link>
              </div>
            </Banner>
          )}

          {resumen.costoIncompleto && (
            <Banner tono="aviso" titulo="Falta algún costo">
              Alguna venta de este periodo no tiene costo registrado, así que los totales de costo
              y ganancia están incompletos. El total de lo vendido sí está completo.
            </Banner>
          )}

          {resumen.costoEstimado && !resumen.costoIncompleto && (
            <Banner tono="info" titulo="Algún costo es estimado">
              En alguna producción se usaron cantidades sin costo propio y se les aplicó el
              promedio conocido.
            </Banner>
          )}

          {resumen.sobrantesLegibles.length > 0 && (
            <Seccion
              titulo="Porciones que no se vendieron"
              descripcion="Su costo salió de tu bolsa igual"
            >
              <Card>
                <dl className="divide-y divide-borde">
                  {resumen.sobrantesLegibles.map((s) => (
                    <Dato
                      key={s.tipo}
                      etiqueta={`${s.etiqueta} (${s.porciones})`}
                      valor={formatearCentavos(s.costoCentavos)}
                    />
                  ))}
                </dl>
              </Card>
            </Seccion>
          )}

          {desglose !== undefined && desglose !== null && (
            <Seccion titulo="Semana por semana">
              <ul className="space-y-2">
                {desglose.semanas
                  .filter((s) => s.resumen.cuantasVentas > 0)
                  .map((s) => (
                    <li key={s.desde}>
                      <Card>
                        <div className="flex items-baseline justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-texto">{s.etiqueta}</p>
                            <p className="text-xs text-texto-tenue">
                              {formatearCorto(s.desde)} – {formatearCorto(s.hasta)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold tabular-nums text-texto">
                              {formatearCentavos(s.resumen.ingresoCentavos)}
                            </p>
                            <p
                              className={`text-xs tabular-nums ${
                                s.resumen.resultadoNetoCentavos < 0 ? 'text-alerta' : 'text-exito'
                              }`}
                            >
                              queda {formatearCentavos(s.resumen.resultadoNetoCentavos)}
                            </p>
                          </div>
                        </div>
                      </Card>
                    </li>
                  ))}
              </ul>
            </Seccion>
          )}

          {porReceta !== undefined && porReceta.length > 1 && (
            <Seccion titulo="Qué platillo deja más" descripcion="Ordenado por ganancia">
              <ul className="space-y-2">
                {porReceta.map((r) => (
                  <li key={r.recipeId}>
                    <Card>
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-texto">{r.nombreReceta}</p>
                          <p className="text-xs text-texto-suave">
                            {r.porcionesVendidas}{' '}
                            {r.porcionesVendidas === 1 ? 'porción' : 'porciones'} ·{' '}
                            {formatearCentavos(r.ingresoCentavos)} de ingreso
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="font-semibold tabular-nums text-texto">
                            {formatearCentavos(r.gananciaCentavos)}
                          </p>
                          {r.margenPorcentaje !== null && (
                            <p className="text-xs text-texto-tenue">
                              {r.margenPorcentaje.toFixed(1)} % de margen
                            </p>
                          )}
                        </div>
                      </div>
                      {r.costoIncompleto && (
                        <p className="mt-1 text-xs text-aviso">Costo incompleto</p>
                      )}
                    </Card>
                  </li>
                ))}
              </ul>
            </Seccion>
          )}

          {ventas !== undefined && ventas.length > 0 && (
            <Seccion titulo="Detalle de las ventas">
              <ul className="space-y-2">
                {ventas.map((renglon) => (
                  <li key={renglon.venta.id}>
                    <Card>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-texto">{renglon.nombreReceta}</p>
                          <p className="text-sm text-texto-suave capitalize">
                            {renglon.fechaLegible}
                          </p>
                          <p className="mt-0.5 text-xs text-texto-tenue">
                            {renglon.venta.porcionesVendidas} a{' '}
                            {formatearCentavos(renglon.venta.precioUnitarioCentavos)}
                            {renglon.venta.costoCentavos !== null && (
                              <> · costo {formatearCentavos(renglon.venta.costoCentavos)}</>
                            )}
                          </p>
                          {renglon.destinos.length > 0 && (
                            <p className="mt-0.5 text-xs text-texto-tenue">
                              {renglon.destinos
                                .map((d) => `${d.porciones} ${d.etiqueta.toLowerCase()}`)
                                .join(' · ')}
                            </p>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="font-semibold tabular-nums text-texto">
                            {formatearCentavos(renglon.venta.ingresoCentavos)}
                          </p>
                          {renglon.costoNoDisponible ? (
                            <p className="text-xs text-texto-tenue">costo n/d</p>
                          ) : (
                            <p className="text-xs text-texto-suave">
                              deja {formatearCentavos(renglon.gananciaCentavos ?? 0)}
                              {renglon.margenPorcentaje !== null &&
                                ` (${renglon.margenPorcentaje.toFixed(0)} %)`}
                            </p>
                          )}
                        </div>
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
            </Seccion>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function PanelResultado({ resumen }: { resumen: ResumenConDestinos }) {
  const perdio = resumen.resultadoNetoCentavos < 0;

  return (
    <Card className={perdio ? 'border-alerta bg-alerta-suave' : 'bg-superficie-suave'}>
      <p className="text-sm text-texto-suave">
        {perdio ? 'Este periodo perdiste' : 'Te quedó'}
      </p>
      <p
        className={`text-3xl font-bold tabular-nums ${perdio ? 'text-alerta' : 'text-texto'}`}
      >
        {formatearCentavos(resumen.resultadoNetoCentavos)}
      </p>

      <dl className="mt-3 divide-y divide-borde">
        {/*
          "Vendido" y no "Ingreso": el numero nace al registrar la venta, este
          pagada o no. Lo que falta cobrar vive en Pedidos, y se avisa abajo.
        */}
        <Dato
          etiqueta="Vendido"
          valor={formatearCentavos(resumen.ingresoCentavos)}
          detalle={`${resumen.porcionesVendidas} ${
            resumen.porcionesVendidas === 1 ? 'porción vendida' : 'porciones vendidas'
          }`}
        />
        <Dato
          etiqueta="Costo de lo vendido"
          valor={formatearCentavos(resumen.costoVendidoCentavos)}
        />
        <Dato
          etiqueta="Costo de lo no vendido"
          valor={formatearCentavos(resumen.costoNoVendidoCentavos)}
          detalle={
            resumen.porcionesNoVendidas > 0
              ? `${resumen.porcionesNoVendidas} ${
                  resumen.porcionesNoVendidas === 1 ? 'porción' : 'porciones'
                }`
              : undefined
          }
        />
        <Dato
          etiqueta="Ganancia de lo vendido"
          valor={formatearCentavos(resumen.gananciaDeLoVendidoCentavos)}
          detalle={
            resumen.margenPorcentaje === null
              ? undefined
              : `${resumen.margenPorcentaje.toFixed(1)} % de margen`
          }
        />
      </dl>

      {perdio && (
        <p className="mt-3 text-xs text-texto-suave">
          El margen de lo que vendiste está bien, pero el costo de las porciones que no se
          vendieron se lo comió.
        </p>
      )}
    </Card>
  );
}
